import { createApp } from "vue";
import App from "./App.vue";
import * as Sentry from "@sentry/vue";

import router from "./router";
import store from "./store";
import Vue3Tour from "vue3-tour";
import "vue3-tour/dist/vue3-tour.css";
import { createI18n } from "vue-i18n";
import { getWorkById } from "@/helpers/toc";

import "@/css/app.css";

import de from "./i18n/de.json";
import en from "./i18n/en.json";

const messages = {
    de,
    en,
};

const i18n = createI18n({
    legacy: false,
    locale: store.state.i18n.locale,
    messages,
    warnHtmlMessage: false,
});

const app = createApp(App);

if (!import.meta.env.DEV) {
    Sentry.init({
        app,
        dsn: "https://68f1dcdc3b456708cb04d50c9d2fb561@o4507159757717504.ingest.de.sentry.io/4511698670649424",
        dataCollection: {},
        enableLogs: true,
    });
}

app.use(store);
app.use(router);
app.use(i18n);
app.use(Vue3Tour);

// ── SEO ──────────────────────────────────────────────────────────────────────

const HOMEPAGE_DESCRIPTION =
    "RKEd – Religiöse Kurzerzählungen. Narrative Vermittlung religiösen Wissens.";

const ROUTE_TITLES = {
    home: "RKEd",
    documents: "RKEd – Korpusübersicht",
    search: "RKEd – Suche",
    bibliography: "RKEd – Literaturverzeichnis",
    names: "RKEd – Namen",
    biblePassages: "RKEd – Bibelstellen",
    motive: "RKEd – Motive",
    directories: "RKEd – Schlagwörter",
    texttoc: "RKEd – Textverzeichnis",
    manuscripttoc: "RKEd – Handschriften",
    incunabletoc: "RKEd – Drucke",
    project: "RKEd – Projekt",
    team: "RKEd – Team",
    network: "RKEd – Netzwerk",
    "help-page": "RKEd – Hilfe",
    impressum: "RKEd – Impressum",
    datenschutz: "RKEd – Datenschutz",
    barrierefreiheit: "RKEd – Barrierefreiheit",
    easylanguage: "RKEd – Leichte Sprache",
    licences: "RKEd – Lizenzen",
    technic: "RKEd – Technik",
    citation: "RKEd – Zitieren",
    abbreviations: "RKEd – Abkürzungen",
    editionPDF: "RKEd – Edition",
    transcribus: "RKEd – Transkribus",
    dvn: "RKEd – DVN",
};

function upsertMetaTag(name, content) {
    let tag = document.head.querySelector(`meta[name="${name}"]`);
    if (!content) {
        tag?.remove();
        return;
    }
    if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", name);
        document.head.appendChild(tag);
    }
    tag.setAttribute("content", content);
}

function upsertCanonicalTag(href) {
    let tag = document.head.querySelector('link[rel="canonical"]');
    if (!tag) {
        tag = document.createElement("link");
        tag.setAttribute("rel", "canonical");
        document.head.appendChild(tag);
    }
    tag.setAttribute("href", href);
}

function buildDocumentSeo(routeId) {
    const id = String(routeId);
    const work = getWorkById(id);
    if (!work) return null;

    const title = work.workTitle || "RKEd";
    const variants = Array.isArray(work.variants) ? work.variants : [];
    const variant =
        variants.find(
            (v) => v && (v.isLeader === true || v.isLeader === "true"),
        ) ||
        variants[0] ||
        null;
    const witness = Array.isArray(variant?.witnesses)
        ? variant.witnesses[0]
        : null;
    const variantId = variant?.id || "";
    const authorItem =
        variant?.authors && variant.authors[0] ? variant.authors[0] : null;
    const author = authorItem?.name?.replace(":", "") || "";
    const authorExcerpt = authorItem?.excerpt ? true : false;
    const authorEdited = authorItem?.edited ? true : false;
    const authorFullTitle =
        authorExcerpt && authorItem?.title ? authorItem.title : "";
    const titleAndAuthor =
        authorFullTitle && author
            ? title + ", Auszug aus " + author + ": " + authorFullTitle
            : authorEdited
              ? title + ", nach " + author
              : (author ? author + ": " : "") + title;
    const keywords = variant?.keywords ? `Keywords: ${variant.keywords}. ` : "";
    const signature = witness?.signature || "";
    const description =
        id +
        " " +
        titleAndAuthor +
        ". " +
        keywords +
        (variantId ? variantId + ": " : "") +
        signature +
        ".";

    return { title, description, author };
}

function updateSeoForRoute(to) {
    const canonical = `${window.location.origin}${to.path}`;

    if (to.name === "document") {
        const rawId = Array.isArray(to.params?.id)
            ? to.params.id[0]
            : to.params?.id;
        if (rawId) {
            const seo = buildDocumentSeo(rawId);
            if (seo) {
                document.title = `RKEd – ${seo.title}`;
                upsertMetaTag("description", seo.description);
                upsertMetaTag("author", seo.author || null);
                upsertCanonicalTag(canonical);
                return;
            }
        }
    }

    const title = ROUTE_TITLES[to.name] || "RKEd";
    document.title = title;
    upsertMetaTag("description", HOMEPAGE_DESCRIPTION);
    upsertMetaTag("author", null);
    upsertCanonicalTag(canonical);
}

// Register before mount so the initial navigation is captured
router.afterEach((to, from) => {
    const body = document.body;
    if (to?.name) body.classList.add(to.name);
    if (from?.name) body.classList.remove(from.name);
    updateSeoForRoute(to);
});

// ─────────────────────────────────────────────────────────────────────────────

app.mount("#app");
